/* eslint-disable */
import { useEffect, useMemo, useReducer, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import {
	TextField,
	Button,
	Switch,
	MenuItem,
	Select,
	IconButton,
	FormControlLabel,
	Tabs,
	Tab,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Chip,
	InputAdornment,
} from '@mui/material';
import { Trash2, Plus, Pencil } from 'lucide-react';
import RetailMenuConfig, { menuBuilderReducer } from './RetailMenu.config';
import { formActions } from '../../Actions';
import { CreatePageHeader } from '../../Components/Headers';
import { urls } from '../../Constants';
import { notificationServices } from '../../Services';
import { isEmpty } from '../../Helpers';
import MenuItemSelection from '../../Components/MenuItemSelection/MenuItemSelection';
import StorePicker from '../../Components/StorePicker/StorePicker';
import retailMenuServices from '../../Services/retailMenu.services';

const FULFILMENT = {
	READY_TO_PICK: 'ready to pick',
	MADE_TO_ORDER: 'made to order',
};
const FULFILMENT_OPTIONS = [
	{ value: '', label: 'Default' },
	{ value: 'READY_TO_PICK', label: 'Ready to pick' },
	{ value: 'MADE_TO_ORDER', label: 'Made to order' },
];

const priceOf = (it) =>
	it.price !== null && it.price !== undefined && it.price !== ''
		? it.price
		: it._sellingPrice;

// normalize a price input to a real number, or null (empty / non-numeric).
const cleanPrice = (v) => {
	if (v === '' || v === null || v === undefined) return null;
	const n = Number(v);
	return Number.isFinite(n) ? n : null;
};

// Upload picker: file -> base64 data URI (backend uploads to S3 on save). `value`
// may be a data URI (new) or a presigned URL (existing) — both preview in <img>.
const ImagePicker = ({ value, onChange, size = 88 }) => {
	const onFile = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		const reader = new FileReader();
		reader.onload = () => onChange(reader.result);
		reader.readAsDataURL(file);
		e.target.value = '';
	};
	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
			{value ? (
				<img
					src={value}
					alt=''
					style={{ width: size, height: size, borderRadius: 10, objectFit: 'cover', border: '1px solid #e5e7eb' }}
				/>
			) : (
				<div
					style={{
						width: size,
						height: size,
						borderRadius: 10,
						background: '#f3f4f6',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						color: '#9ca3af',
						fontSize: 12,
					}}
				>
					No image
				</div>
			)}
			<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
				<Button variant='outlined' size='small' component='label' sx={{ textTransform: 'none' }}>
					{value ? 'Change image' : 'Upload image'}
					<input
						type='file'
						accept='image/png,image/jpeg,image/jpg,image/webp'
						hidden
						onChange={onFile}
					/>
				</Button>
				{value ? (
					<Button size='small' color='inherit' onClick={() => onChange('')} sx={{ textTransform: 'none' }}>
						Remove
					</Button>
				) : null}
			</div>
		</div>
	);
};

// ---- small dialog to edit a single item -------------------------------------
const ItemEditDialog = ({ item, categories, onClose, onSave, onDelete }) => {
	// Seeded once at mount; the parent remounts this per item via `key`, so an
	// async parent re-render can't reset the in-progress draft (incl. price).
	const [draft, setDraft] = useState(item || {});
	if (!item) return null;
	const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
	return (
		<Dialog open onClose={onClose} maxWidth='xs' fullWidth>
			<DialogTitle sx={{ pb: 0.5 }}>
				<div style={{ fontSize: 16, fontWeight: 600 }}>
					{draft._productName || draft.displayName || 'Item'}
				</div>
				<div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>
					{draft._variantName ? `${draft._variantName} · ` : ''}
					variant #{draft._clientVariantId || '—'}
				</div>
			</DialogTitle>
			<DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
				<ImagePicker value={draft.imageUrl || ''} onChange={(v) => set('imageUrl', v)} />
				<TextField
					size='small'
					fullWidth
					label='Display name (optional)'
					value={draft.displayName || ''}
					onChange={(e) => set('displayName', e.target.value)}
				/>
				<TextField
					size='small'
					fullWidth
					label='Price'
					type='number'
					value={draft.price ?? ''}
					onChange={(e) => set('price', e.target.value)}
					placeholder={`${draft._sellingPrice ?? ''}`}
					helperText={`Blank = variant price ₹${draft._sellingPrice ?? '-'}`}
					InputProps={{ startAdornment: <InputAdornment position='start'>₹</InputAdornment> }}
				/>
				<TextField
					size='small'
					fullWidth
					select
					label='Fulfilment'
					value={draft.fulfilmentType || ''}
					onChange={(e) => set('fulfilmentType', e.target.value || null)}
				>
					{FULFILMENT_OPTIONS.map((o) => (
						<MenuItem key={o.value} value={o.value}>
							{o.label}
						</MenuItem>
					))}
				</TextField>
				<TextField
					size='small'
					fullWidth
					select
					label='Category'
					value={draft.category || ''}
					onChange={(e) => set('category', e.target.value)}
				>
					{(categories || []).map((c) => (
						<MenuItem key={c} value={c}>
							{c}
						</MenuItem>
					))}
				</TextField>
				<FormControlLabel
					control={
						<Switch
							size='small'
							checked={!!draft.isAvailable}
							onChange={(e) => set('isAvailable', e.target.checked)}
						/>
					}
					label='Available'
				/>
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
				<Button color='error' startIcon={<Trash2 size={15} />} onClick={onDelete}>
					Remove
				</Button>
				<div>
					<Button color='inherit' onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant='contained'
						onClick={() =>
							onSave({
								displayName: draft.displayName,
								price: cleanPrice(draft.price),
								fulfilmentType: draft.fulfilmentType || null,
								category: draft.category,
								imageUrl: draft.imageUrl || '',
								isAvailable: draft.isAvailable,
							})
						}
					>
						Save
					</Button>
				</div>
			</DialogActions>
		</Dialog>
	);
};

const CreateRetailMenu = (props) => {
	const {
		isEdit,
		data,
		productsList,
		storesList,
		fetchProducts,
		fetchStores,
		saveMenu,
		fetchMenu,
		breadcrumbs,
	} = props;

	const [state, dispatch] = useReducer(menuBuilderReducer, RetailMenuConfig.initialState);
	const { fields } = state;
	const menuId = useParams().id;
	const history = useHistory();

	const [tab, setTab] = useState(0);
	const [selectedCat, setSelectedCat] = useState('');
	const [newCat, setNewCat] = useState('');
	const [pickerOpen, setPickerOpen] = useState(false);
	const [editIdx, setEditIdx] = useState(null); // flat index of item being edited
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		fetchProducts({
			entity: 'ALL_PRODUCTS',
			req: { params: { id: '', variants: true } },
			method: 'LIST',
		});
		fetchStores({ entity: 'STORES', req: { params: { id: '' } }, method: 'LIST' });
		if (isEdit) {
			fetchMenu({ entity: 'RETAIL_MENU', req: { params: { id: menuId } }, method: 'VIEW' });
		}
	}, []);

	useEffect(() => {
		if (isEdit && !isEmpty(data) && String(data.id) === String(menuId)) {
			dispatch({ type: 'INIT', payload: data });
		}
	}, [data]);

	// keep a valid selected category
	useEffect(() => {
		const cats = fields.categories || [];
		if (cats.length && !cats.includes(selectedCat)) setSelectedCat(cats[0]);
		if (!cats.length && selectedCat) setSelectedCat('');
	}, [fields.categories]);

	const existingVariantIds = useMemo(
		() => (fields.items || []).map((it) => it.variantId),
		[fields.items]
	);

	// variantId -> display meta, so saved items (which persist only variantId)
	// still show product name / variant / clientVariantId / price on view+edit.
	const variantIndex = useMemo(() => {
		const m = {};
		(productsList || []).forEach((p) => {
			(p.variants || []).forEach((v) => {
				m[v.id] = {
					_productName: p.productName,
					_variantName: v.displayName || v.size || '',
					_clientVariantId: v.clientVariantId || v.id,
					_sellingPrice: v.sellingPrice ?? v.price ?? null,
				};
			});
		});
		return m;
	}, [productsList]);

	// fill display-only fields from the catalogue when the item lacks them
	const withMeta = (it) => {
		if (!it) return it;
		const meta = variantIndex[it.variantId] || {};
		return {
			...it,
			_productName: it._productName || meta._productName,
			_variantName: it._variantName || meta._variantName,
			_clientVariantId: it._clientVariantId || meta._clientVariantId,
			_sellingPrice: it._sellingPrice != null ? it._sellingPrice : meta._sellingPrice,
		};
	};

	// items of the selected category, tagged with their flat index
	const catItems = useMemo(
		() =>
			(fields.items || [])
				.map((it, idx) => ({ it, idx }))
				.filter(({ it }) => (it.category || '') === selectedCat),
		[fields.items, selectedCat]
	);

	const countByCat = (c) =>
		(fields.items || []).filter((it) => (it.category || '') === c).length;

	const setField = (name, value) => dispatch({ type: 'SET_FIELD', name, value });

	const addCategory = () => {
		const name = newCat.trim();
		if (!name) return;
		dispatch({ type: 'ADD_CATEGORY', name });
		setSelectedCat(name);
		setNewCat('');
	};

	const handleCancel = () => history.push(`${urls.EXTENSION}${urls.RETAIL_MENU}`);

	const handleSave = async (e) => {
		e?.preventDefault?.();
		if (!fields.name?.trim()) {
			notificationServices.generateNotification({ type: 'error', message: 'Please enter a menu name' });
			setTab(1);
			return;
		}
		const req = { data: RetailMenuConfig.deserializer(fields) };
		const method = isEdit ? 'UPDATE' : 'CREATE';
		if (isEdit) req.params = { id: menuId };
		// Call the service directly so backend errors surface (the generic gateway
		// swallowed 300/500s here).
		setSaving(true);
		try {
			const res = await retailMenuServices.gateway({ req, method });
			if (res?.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: res?.message || 'Menu saved',
				});
				history.push(`${urls.EXTENSION}${urls.RETAIL_MENU}`);
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: res?.message || 'Could not save menu',
				});
			}
		} catch (err) {
			notificationServices.generateNotification({
				type: 'error',
				message: err?.message || 'Could not save menu',
			});
		} finally {
			setSaving(false);
		}
	};

	const editItem = editIdx !== null ? withMeta(fields.items[editIdx]) : null;

	return (
		<div className='root' style={{ paddingBottom: 40 }}>
			<form onSubmit={handleSave}>
				<CreatePageHeader
					heading={isEdit ? 'Menu builder' : 'Menu builder'}
					handleCancel={handleCancel}
					breadcrumbs={[
						...(breadcrumbs || []),
						{ link: '', name: isEdit ? `Edit ${fields.name || 'menu'}` : 'Create menu' },
					]}
					showBreadcrumbs
				/>

				<div style={{ padding: 16 }}>
					<div style={{ border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
						{/* Card header */}
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								padding: '16px 20px',
								borderBottom: '1px solid #f3f4f6',
							}}
						>
							<div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>
								{fields.name || 'Untitled menu'}
							</div>
							<Chip
								label={fields.isEnabled ? 'Enabled' : 'Disabled'}
								color={fields.isEnabled ? 'primary' : 'default'}
								variant={fields.isEnabled ? 'filled' : 'outlined'}
								onClick={() => setField('isEnabled', !fields.isEnabled)}
								sx={{ cursor: 'pointer', fontWeight: 600 }}
							/>
						</div>

						{/* Tabs */}
						<Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ px: 2, minHeight: 44 }}>
							<Tab label='Categories & items' sx={{ textTransform: 'none' }} />
							<Tab label='Menu details' sx={{ textTransform: 'none' }} />
							<Tab label={`Stores (${(fields.storeIds || []).length})`} sx={{ textTransform: 'none' }} />
						</Tabs>

						{/* scrollable body — header/tabs/footer stay fixed, page doesn't scroll */}
						<div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
						{/* ---- Tab 0: Categories & items ---- */}
						{tab === 0 && (
							<div style={{ display: 'flex', minHeight: 340 }}>
								{/* category rail */}
								<div style={{ width: 220, borderRight: '1px solid #f3f4f6', padding: 12 }}>
									<div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: 0.5, padding: '4px 8px' }}>
										CATEGORIES
									</div>
									{(fields.categories || []).map((c) => (
										<div
											key={c}
											onClick={() => setSelectedCat(c)}
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: 6,
												padding: '8px 8px',
												borderRadius: 8,
												cursor: 'pointer',
												background: selectedCat === c ? '#eff6ff' : 'transparent',
												color: selectedCat === c ? '#1d4ed8' : '#374151',
												fontWeight: selectedCat === c ? 600 : 500,
												fontSize: 14,
											}}
										>
											<span style={{ flex: 1 }}>{c}</span>
											<span style={{ fontSize: 11, color: '#9ca3af' }}>{countByCat(c)}</span>
											<IconButton
												size='small'
												onClick={(e) => {
													e.stopPropagation();
													dispatch({ type: 'REMOVE_CATEGORY', name: c });
												}}
											>
												<Trash2 size={13} />
											</IconButton>
										</div>
									))}
									<div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
										<TextField
											size='small'
											variant='standard'
											placeholder='Add category'
											value={newCat}
											onChange={(e) => setNewCat(e.target.value)}
											onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
											sx={{ flex: 1 }}
										/>
										<IconButton size='small' color='primary' onClick={addCategory}>
											<Plus size={16} />
										</IconButton>
									</div>
								</div>

								{/* items pane */}
								<div style={{ flex: 1, padding: 16 }}>
									{!selectedCat ? (
										<div style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center', paddingTop: 60 }}>
											Add a category to start building the menu.
										</div>
									) : (
										<>
											<div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
												<div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
													{selectedCat}
													<span style={{ fontSize: 13, color: '#9ca3af', fontWeight: 400 }}>
														{'  '}· {catItems.length} items
													</span>
												</div>
												<div style={{ flex: 1 }} />
												<Button
													size='small'
													variant='outlined'
													startIcon={<Plus size={16} />}
													onClick={() => setPickerOpen(true)}
													sx={{ textTransform: 'none' }}
												>
													Add item
												</Button>
											</div>

											{catItems.length === 0 ? (
												<div style={{ color: '#9ca3af', fontSize: 13, padding: '24px 0' }}>
													No items — use “Add item”.
												</div>
											) : (
												<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
													{catItems.map(({ it: raw, idx }) => {
														const it = withMeta(raw);
														return (
														<div
															key={it.variantId}
															style={{
																display: 'flex',
																alignItems: 'center',
																gap: 12,
																border: '1px solid #f3f4f6',
																borderRadius: 12,
																padding: '12px 14px',
															}}
														>
															<span
																style={{
																	width: 8,
																	height: 8,
																	borderRadius: 8,
																	background: it.isAvailable ? '#22c55e' : '#d1d5db',
																	flexShrink: 0,
																}}
															/>
															<div style={{ flex: 1, minWidth: 0 }}>
																<div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
																	{it.displayName || it._productName}
																	{it._variantName ? ` · ${it._variantName}` : ''}
																</div>
																<div style={{ fontSize: 12, color: '#9ca3af' }}>
																	variant #{it._clientVariantId}
																	{it.fulfilmentType ? ` · ${FULFILMENT[it.fulfilmentType]}` : ''}
																</div>
															</div>
															<div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
																₹{priceOf(it) ?? '-'}
															</div>
															<IconButton size='small' onClick={() => setEditIdx(idx)}>
																<Pencil size={15} />
															</IconButton>
														</div>
														);
													})}
												</div>
											)}
										</>
									)}
								</div>
							</div>
						)}

						{/* ---- Tab 1: Menu details ---- */}
						{tab === 1 && (
							<div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 520 }}>
								<TextField
									size='small'
									label='Menu name'
									required
									value={fields.name}
									onChange={(e) => setField('name', e.target.value)}
								/>
								<TextField
									size='small'
									label='Description'
									value={fields.description}
									onChange={(e) => setField('description', e.target.value)}
								/>
								<div>
									<label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>
										Menu image
									</label>
									<div style={{ marginTop: 8 }}>
										<ImagePicker
											value={fields.imageUrl || ''}
											onChange={(v) => setField('imageUrl', v)}
										/>
									</div>
								</div>
								<FormControlLabel
									control={
										<Switch
											size='small'
											checked={!!fields.isEnabled}
											onChange={(e) => setField('isEnabled', e.target.checked)}
										/>
									}
									label='Enabled'
								/>
							</div>
						)}

						{/* ---- Tab 2: Stores ---- */}
						{tab === 2 && (
							<div style={{ padding: 20, maxWidth: 560 }}>
								<label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>
									Serve at stores
								</label>
								<div style={{ marginTop: 8 }}>
									<StorePicker
										multiple
										stores={storesList || []}
										value={fields.storeIds || []}
										onChange={(ids) => dispatch({ type: 'SET_STORES', payload: ids })}
										label='Stores'
										helperText='A menu can serve many stores, but each store serves only one menu — adding a store here moves it from its previous menu.'
									/>
								</div>
							</div>
						)}
						</div>

						{/* footer */}
						<div
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								gap: 10,
								padding: '14px 20px',
								borderTop: '1px solid #f3f4f6',
							}}
						>
							<Button color='inherit' onClick={handleCancel} sx={{ textTransform: 'none' }}>
								Cancel
							</Button>
							<Button type='submit' variant='contained' disabled={saving} sx={{ textTransform: 'none' }}>
								{saving ? 'Saving…' : 'Save menu'}
							</Button>
						</div>
					</div>
				</div>
			</form>

			<MenuItemSelection
				open={pickerOpen}
				onClose={() => setPickerOpen(false)}
				productsList={productsList || []}
				existingVariantIds={existingVariantIds}
				targetCategory={selectedCat}
				onAdd={(items) => dispatch({ type: 'ADD_ITEMS', category: selectedCat, items })}
			/>

			{editItem && (
				<ItemEditDialog
					key={editIdx}
					item={editItem}
					categories={fields.categories}
					onClose={() => setEditIdx(null)}
					onSave={(patch) => {
						dispatch({ type: 'UPDATE_ITEM', itemIndex: editIdx, patch });
						setEditIdx(null);
					}}
					onDelete={() => {
						dispatch({ type: 'REMOVE_ITEM', itemIndex: editIdx });
						setEditIdx(null);
					}}
				/>
			)}
		</div>
	);
};

const mapStateToProps = (state) => ({
	data: state.form.RETAIL_MENU.data.VIEW,
	productsList: state.form.ALL_PRODUCTS.data.LIST,
	storesList: state.form.STORES.data.LIST,
});

const mapDispatchToProps = {
	fetchProducts: formActions.gateway,
	fetchStores: formActions.gateway,
	saveMenu: formActions.gateway,
	fetchMenu: formActions.gateway,
};

CreateRetailMenu.defaultProps = { isEdit: false };

export default connect(mapStateToProps, mapDispatchToProps)(CreateRetailMenu);
